// src/features/analytics/components/SmartAnalytics/SmartAnalytics.jsx
import React from 'react';
import { Card, Row, Col, Progress, Tag, List, Alert, Statistic } from 'antd';
import {
    TrophyOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined
} from '@ant-design/icons';
import { analyticsService } from '../../../../shared/services/analyticsService';
import { formatCurrency } from '../../../../shared/utils/formatters';
import styles from './SmartAnalytics.module.css';

export default function SmartAnalytics({ debts, payments }) {
    const habits = analyticsService.analyzeDebtHabits(debts, payments);
    const prediction = analyticsService.predictPaymentCapability(debts, payments);
    const paymentPlan = analyticsService.generateOptimalPaymentPlan(debts);

    const getScoreColor = (score) => {
        if (score >= 80) return '#52c41a';
        if (score >= 60) return '#faad14';
        if (score >= 40) return '#fa8c16';
        return '#ff4d4f';
    };

    const getRiskColor = (level) => {
        switch (level) {
            case 'low': return 'success';
            case 'medium': return 'warning';
            case 'high': return 'error';
            default: return 'default';
        }
    };

    const getUrgencyIcon = (urgency) => {
        switch (urgency) {
            case 'critical': return <WarningOutlined style={{ color: '#ff4d4f' }} />;
            case 'high': return <ClockCircleOutlined style={{ color: '#fa8c16' }} />;
            case 'medium': return <ClockCircleOutlined style={{ color: '#faad14' }} />;
            default: return <CheckCircleOutlined style={{ color: '#52c41a' }} />;
        }
    };

    return (
        <div className={styles.smartAnalytics}>
            <Row gutter={[16, 16]}>
                {/* Phân tích thói quen */}
                <Col xs={24} lg={8}>
                    <Card title="🧠 Phân tích thói quen vay nợ" className={styles.analysisCard}>
                        <div className={styles.habitItem}>
                            <span>Số tiền vay trung bình:</span>
                            <strong>{formatCurrency(habits.averageDebtAmount)}</strong>
                        </div>

                        <div className={styles.habitItem}>
                            <span>Người cho vay thường xuyên:</span>
                            <strong>{habits.mostCommonLender}</strong>
                        </div>

                        <div className={styles.habitItem}>
                            <span>Thời gian trả trung bình:</span>
                            <strong>{habits.averagePaymentTime} ngày</strong>
                        </div>

                        <div className={styles.habitItem}>
                            <span>Tần suất vay:</span>
                            <strong>{habits.debtFrequency} lần/tháng</strong>
                        </div>

                        <div className={styles.habitItem}>
                            <span>Mức độ rủi ro:</span>
                            <Tag color={getRiskColor(habits.riskLevel)}>
                                {habits.riskLevel === 'low' ? 'Thấp' :
                                    habits.riskLevel === 'medium' ? 'Trung bình' : 'Cao'}
                            </Tag>
                        </div>

                        <div className={styles.purposeList}>
                            <h4>Mục đích vay phổ biến:</h4>
                            {habits.commonPurposes.map((item, index) => (
                                <div key={index} className={styles.purposeItem}>
                                    <span>{item.purpose}</span>
                                    <Tag>{item.count} lần</Tag>
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>

                {/* Dự đoán khả năng trả nợ */}
                <Col xs={24} lg={8}>
                    <Card title="🎯 Dự đoán khả năng trả nợ" className={styles.predictionCard}>
                        <div className={styles.scoreSection}>
                            <Progress
                                type="circle"
                                percent={prediction.score}
                                strokeColor={getScoreColor(prediction.score)}
                                format={percent => `${percent.toFixed(0)}`}
                                size={120}
                            />
                            <div className={styles.scoreLabel}>
                                Điểm tín dụng: <strong>{prediction.level === 'excellent' ? 'Xuất sắc' :
                                    prediction.level === 'good' ? 'Tốt' :
                                        prediction.level === 'fair' ? 'Khá' : 'Kém'}</strong>
                            </div>
                        </div>

                        {prediction.factors.length > 0 && (
                            <div className={styles.factorsSection}>
                                <h4>Yếu tố ảnh hưởng:</h4>
                                <List
                                    size="small"
                                    dataSource={prediction.factors}
                                    renderItem={item => (
                                        <List.Item>
                                            <WarningOutlined style={{ color: '#faad14', marginRight: 8 }} />
                                            {item}
                                        </List.Item>
                                    )}
                                />
                            </div>
                        )}

                        {prediction.recommendations.length > 0 && (
                            <Alert
                                message="Khuyến nghị"
                                description={
                                    <ul>
                                        {prediction.recommendations.map((rec, index) => (
                                            <li key={index}>{rec}</li>
                                        ))}
                                    </ul>
                                }
                                type="info"
                                showIcon
                            />
                        )}
                    </Card>
                </Col>

                {/* Kế hoạch trả nợ tối ưu */}
                <Col xs={24} lg={8}>
                    <Card title="📋 Kế hoạch trả nợ tối ưu" className={styles.planCard}>
                        {paymentPlan.length === 0 ? (
                            <div className={styles.noDebt}>
                                <TrophyOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                                <h3>Chúc mừng!</h3>
                                <p>Bạn không có nợ nào cần trả</p>
                            </div>
                        ) : (
                            <List
                                dataSource={paymentPlan.slice(0, 5)}
                                renderItem={item => (
                                    <List.Item className={styles.planItem}>
                                        <div className={styles.planHeader}>
                                            <span className={styles.rank}>#{item.rank}</span>
                                            {getUrgencyIcon(item.urgency)}
                                            <span className={styles.lender}>{item.debt.nguoiVay}</span>
                                        </div>

                                        <div className={styles.planDetails}>
                                            <div>Còn lại: <strong>{formatCurrency(item.debt.remaining)}</strong></div>
                                            <div>Nên trả: <strong>{formatCurrency(item.debt.suggestedPayment)}</strong></div>
                                            <div className={styles.strategy}>{item.strategy}</div>
                                        </div>
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
}
